import { Address } from '@ui/types/alias';
import { Nullable } from '@ui/types/utility';
import { keccak256, toHex } from 'viem';

/**
 * Build a composite key like "k1:v1|k2:v2|k3:v3".
 * Order follows the object's own property insertion order.
 * Undefined / null become '_'.
 * Addresses (0x...) lowercased. Strings trimmed & lowercased (except hex / already upper-lower mix).
 */
export interface BuildObjectKeyOptions {
  pairSep?: string; // default '|'
  kvSep?: string; // default ':'
  transformKey?: (k: string) => string;
  transformValue?: (v: unknown, k: string) => string;
  skipUndefined?: boolean;
}

const defaultTransformValue = (v: unknown): string => {
  if (v === null || v === undefined) return '_';
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === '') return '_';

    return s.toLowerCase();
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number' || typeof v === 'bigint') return String(v);

  return JSON.stringify(v);
};

export const buildObjectKey = (
  obj: Record<string, any>,
  {
    pairSep = '|',
    kvSep = ':',
    transformKey = (k: string) => k,
    transformValue = defaultTransformValue,
    skipUndefined = false,
  }: BuildObjectKeyOptions = {},
): string => {
  const parts: string[] = [];
  for (const k of Object.keys(obj)) {
    const val = obj[k];
    if (skipUndefined && (val === undefined || val === null)) continue;
    parts.push(`${transformKey(k)}${kvSep}${transformValue(val, k)}`);
  }

  const unifyStr = parts.join(pairSep);

  return keccak256(toHex(unifyStr));
};

/**
 * Convenience wrapper specifically for open market trade params.
 * Normalizes:
 *  - isLong -> 'long' / 'short'
 *  - addresses lowercased
 */
export interface TradeKeyParams {
  pairBase: Nullable<Address>;
  isLong: Nullable<boolean>;
  stopLoss: Nullable<bigint>;
  takeProfit: Nullable<bigint>;
  tokenIn: Nullable<Address>;

  randomHexValue: string;
}

export const buildTradeKey = (p: TradeKeyParams) => {
  return buildObjectKey({
    isLong: p.isLong,
    pairBase: p.pairBase,
    stopLoss: p.stopLoss,
    takeProfit: p.takeProfit,
    tokenIn: p.tokenIn,
    randomHexValue: p.randomHexValue,
  });
};
